import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { classnames } from '@bydesign-ui/utils';
import { useRefGetter } from '@bydesign-ui/hooks';
import { usePrefixCls } from '@byte-design/config';
import { useCascader, useCascaderExpanded, useCascaderItem } from '../../../hooks/tree';
import { useSecondChildExpandAnimation } from '../../../hooks/useSecondChildExpandAnimation';
import { TreeInfoContext } from '../context/TreeInfoContext';
import { HighlightKeyword } from '../../util/HighlightKeyword';
import { Item } from './Item';

var defaultRenderItem = function defaultRenderItem(info, highlightLabel) {
  return highlightLabel;
};

var Effect = typeof window === 'undefined' ? useEffect : useLayoutEffect;
export function TreeNode(props) {
  var treeInfo = useContext(TreeInfoContext);
  var id = props.id,
      index = props.index,
      markKey = props.markKey,
      _props$nodeKey = props.nodeKey,
      nodeKey = _props$nodeKey === void 0 ? 'id' : _props$nodeKey;
  var node = useCascaderItem(id);
  var tree = useCascader();
  var isLeaf = node.isLeaf,
      state = node.state,
      level = node.level,
      disabled = node.disabled,
      children = node.children;
  var showLine = treeInfo.showLine,
      search = treeInfo.search,
      renderIcon = treeInfo.renderIcon,
      _treeInfo$renderItem = treeInfo.renderItem,
      renderItem = _treeInfo$renderItem === void 0 ? defaultRenderItem : _treeInfo$renderItem;
  var expandedIds = useCascaderExpanded();
  var expanded = expandedIds.indexOf(id) > -1;
  var highlightLabel = useMemo(function () {
    return React.createElement(HighlightKeyword, {
      label: node.label,
      keyword: search
    });
  }, [node.label, search]);
  var hasLine = useMemo(function () {
    if (!showLine || isLeaf || !node.parentId) {
      return false;
    }

    var parentNode = tree.getItemInfo(node.parentId);
    var nextNode = parentNode.children[index + 1];
    return !!nextNode && !nextNode.isLeaf;
  }, [showLine, isLeaf, index]);
  var cls = classnames(usePrefixCls("tree-node-level-".concat(level)), usePrefixCls('tree-node', {
    leaf: isLeaf,
    closed: !expanded && !isLeaf,
    line: hasLine
  }));
  var clsBody = usePrefixCls('tree-node-body');
  var style = {
    marginLeft: "".concat(level > 1 ? 16 : 0, "px")
  };
  var elementRef = useRef(null);
  var startExpandAnimation = useSecondChildExpandAnimation(elementRef, {
    disabled: isLeaf,
    expanded: expanded
  });
  var Icon = useMemo(function () {
    return renderIcon ? renderIcon(node, expanded) : undefined;
  }, [renderIcon, expanded, markKey]);
  var Label = useMemo(function () {
    return renderItem(node, highlightLabel);
  }, [renderItem, markKey]);
  useEffect(function () {
    tree.setItemRef(id, elementRef);
  }, []);
  Effect(startExpandAnimation, [expanded, isLeaf]);
  var selectToExpand = tree.getSelectToExpand();
  var fromSelectedValue = tree.getFromSelectedValue();
  var singleFromValue = fromSelectedValue[0];
  var expandedRefGetter = useRefGetter(expanded);
  useEffect(function () {
    if (selectToExpand && !tree.multiple && !isLeaf && !expandedRefGetter() && node.children && node.children.some(function (item) {
      return item.value === singleFromValue;
    })) {
      tree.setExpanded(id, true);
    }
  }, [selectToExpand, tree.multiple, isLeaf, singleFromValue]);

  if (!tree.matchTreeSearch(id)) {
    return null;
  }

  return React.createElement("div", {
    className: cls,
    ref: elementRef,
    style: style
  }, React.createElement(Item, {
    id: id,
    loading: node.loading,
    isLeaf: isLeaf,
    showLine: showLine,
    icon: Icon,
    label: markKey === 'id' ? renderItem(node, highlightLabel) : Label,
    state: state,
    expanded: expanded,
    disabled: disabled
  }), !isLeaf && React.createElement("div", {
    className: clsBody
  }, children.map(function (child, index) {
    return React.createElement(TreeNode, {
      id: child.id,
      index: index,
      key: child[nodeKey],
      markKey: child[nodeKey],
      nodeKey: nodeKey
    });
  })));
}